import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { ShieldCheck, Target, Users, BookOpen, Scale } from "lucide-react";

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title="À propos — Notre mission"
        description="Découvrez l'histoire de Fiable ou Pas, notre mission pour aider les consommateurs à vérifier les sites e-commerce et nos engagements pour un web plus sûr."
        path="/a-propos"
      />

      <Header />

      <main className="flex-1 py-12 bg-[#F9FAFB]">
        <div className="container-app max-w-4xl">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A]">
              À propos
            </span>

            <h1 className="text-4xl sm:text-5xl font-extrabold font-heading text-slate-900 tracking-tight mt-3 mb-4">
              Notre mission
            </h1>

            <p className="text-slate-600 text-base max-w-2xl mx-auto leading-relaxed">
              Fiable ou Pas est né d&apos;un constat simple : acheter en ligne est devenu un acte du quotidien, mais identifier les sites frauduleux reste complexe pour beaucoup de consommateurs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="card-base p-8 border-t-4 border-t-[#1E3A8A]">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-[#1E3A8A] flex items-center justify-center mb-6">
                <Target className="w-6 h-6" />
              </div>

              <h2 className="text-xl font-bold font-heading text-slate-900 mb-4">
                Pourquoi ce site ?
              </h2>

              <p className="text-sm text-slate-600 leading-relaxed">
                Chaque année, des milliers de Français sont victimes d&apos;arnaques sur de faux sites e-commerce. Ces sites utilisent souvent les mêmes codes visuels que les grandes marques pour tromper la vigilance des consommateurs. Notre objectif est de fournir un outil rapide, gratuit et compréhensible pour aider à lever le doute avant l&apos;achat.
              </p>
            </div>

            <div className="card-base p-8 border-t-4 border-t-green-500">
              <div className="w-12 h-12 rounded-xl bg-green-100 text-green-700 flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6" />
              </div>

              <h2 className="text-xl font-bold font-heading text-slate-900 mb-4">
                Notre approche
              </h2>

              <p className="text-sm text-slate-600 leading-relaxed">
                Nous ne nous contentons pas de donner un simple &quot;avis&quot;. Notre moteur d&apos;analyse vérifie automatiquement plus de 12 signaux techniques, légaux et de cohérence : HTTPS, mentions légales, CGV, coordonnées, informations société, âge du domaine, réputation externe et autres éléments utiles pour évaluer un niveau de risque.
              </p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none space-y-8 bg-white p-8 sm:p-12 rounded-2xl shadow-sm border border-slate-100 mb-16">
            <section>
              <h2 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight">
                Qui sommes-nous ?
              </h2>

              <p className="text-slate-600 leading-relaxed">
                Fiable ou Pas est un projet indépendant créé par David Ramaugé, passionné par le web, les outils numériques et la protection des consommateurs. L&apos;idée est de mettre la technologie au service de la vigilance en ligne, avec un outil accessible aussi bien aux utilisateurs expérimentés qu&apos;aux personnes moins à l&apos;aise avec les vérifications techniques.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight">
                Un outil d&apos;aide à la décision
              </h2>

              <div className="flex items-start gap-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-5">
                <div className="mt-1 bg-white p-2 rounded-xl flex-shrink-0 text-[#1E3A8A]">
                  <Scale className="w-5 h-5" />
                </div>

                <p className="text-slate-700 leading-relaxed m-0">
                  Fiable ou Pas est un outil d&apos;aide à la décision. Il ne remplace pas une vérification officielle, un avis juridique, une décision d&apos;autorité ou les démarches auprès des organismes compétents. Le score affiché doit être utilisé comme un indicateur de vigilance, à croiser avec votre propre jugement avant tout achat.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight">
                Nos engagements
              </h2>

              <ul className="space-y-4 m-0 p-0 list-none">
                <li className="flex items-start gap-4">
                  <div className="mt-1 bg-blue-50 p-1 rounded flex-shrink-0">
                    <Users className="w-4 h-4 text-[#1E3A8A]" />
                  </div>

                  <div className="text-slate-600">
                    <strong className="text-slate-800">Neutralité :</strong>{" "}
                    Nous n&apos;acceptons aucune rémunération des sites analysés pour modifier, améliorer ou influencer leur score.
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="mt-1 bg-blue-50 p-1 rounded flex-shrink-0">
                    <BookOpen className="w-4 h-4 text-[#1E3A8A]" />
                  </div>

                  <div className="text-slate-600">
                    <strong className="text-slate-800">Pédagogie :</strong>{" "}
                    Chaque score est accompagné d&apos;explications détaillées afin d&apos;aider l&apos;utilisateur à comprendre les signaux détectés et à développer ses propres réflexes de vérification.
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <div className="mt-1 bg-blue-50 p-1 rounded flex-shrink-0">
                    <ShieldCheck className="w-4 h-4 text-[#1E3A8A]" />
                  </div>

                  <div className="text-slate-600">
                    <strong className="text-slate-800">Transparence :</strong>{" "}
                    Notre méthode d&apos;analyse est expliquée en détail sur notre page &quot;Comment ça marche&quot;. Nous indiquons également lorsqu&apos;une vérification n&apos;a pas pu être réalisée automatiquement.
                  </div>
                </li>
              </ul>
            </section>

            <section className="pt-8 border-t border-slate-100">
              <h2 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight">
                Nous contacter
              </h2>

              <p className="text-slate-600 leading-relaxed">
                Vous avez une question, une suggestion ou vous souhaitez nous signaler une erreur d&apos;analyse ? Vous pouvez nous écrire via notre{" "}
                <a href="/contact" className="text-[#1E3A8A] underline font-bold">
                  formulaire de contact
                </a>.
              </p>

              <p className="text-slate-600 leading-relaxed">
                Pour une fraude avérée, un litige consommateur ou une demande officielle, nous recommandons également de contacter les plateformes compétentes comme{" "}
                <a
                  href="https://signal.conso.gouv.fr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1E3A8A] underline font-bold"
                >
                  SignalConso
                </a>{" "}
                ou les autorités adaptées à votre situation.
              </p>
            </section>
          </div>

          <div className="text-center text-xs text-slate-400">
            Dernière mise à jour : mai 2026
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}